import type { Article, ArticleFormPayload, TroubleshootingRow } from '../types/article.types'
import type { ArticleFormState } from '../types/article-form.types'

export const EMPTY_TROUBLESHOOTING_ROW: TroubleshootingRow = {
  issue: '',
  possible_cause: '',
  recommended_action: '',
}

type BuildArticlePayloadOptions = {
  isCustomerSpecific: boolean
  pendingDeletedAttachmentIds: number[]
}

export function createEmptyForm(): ArticleFormState {
  return {
    title: '',
    summary: '',
    category: '',
    tags: [],
    rich_text_body: '',
    discipline: '',
    technote_type: '',
    scope: '',
    customer: '',
    confidentiality: '',
    brand: '',
    product_family: '',
    model_number: '',
    asset_type: '',
    technology_type: '',
    machine: '',
    version: '',
    problem: '',
    symptoms: '',
    root_cause: '',
    solution: '',
    troubleshooting_notes: [{ ...EMPTY_TROUBLESHOOTING_ROW }],
    external_links: '',
    author_id: null,
    reviewer_id: null,
  }
}

export function normalizeTag(tag: string) {
  return tag.trim().toLowerCase()
}

export function dedupeTags(tags: string[]) {
  const seen = new Set<string>()

  return tags.reduce<string[]>((uniqueTags, tag) => {
    const trimmedTag = tag.trim()
    if (!trimmedTag) {
      return uniqueTags
    }

    const normalizedTag = normalizeTag(trimmedTag)
    if (seen.has(normalizedTag)) {
      return uniqueTags
    }

    seen.add(normalizedTag)
    uniqueTags.push(trimmedTag)
    return uniqueTags
  }, [])
}

export function parseTags(tags?: string) {
  try {
    return tags ? dedupeTags(JSON.parse(tags) as string[]) : []
  } catch {
    return []
  }
}

export function parseTroubleshootingRows(troubleshootingNotes?: string) {
  try {
    const parsed = troubleshootingNotes ? (JSON.parse(troubleshootingNotes) as TroubleshootingRow[]) : []
    return parsed.length ? parsed : [{ ...EMPTY_TROUBLESHOOTING_ROW }]
  } catch {
    return [{ ...EMPTY_TROUBLESHOOTING_ROW }]
  }
}

export function mapArticleToForm(article: Article): ArticleFormState {
  return {
    title: article.title || '',
    summary: article.summary || '',
    category: article.category || '',
    tags: parseTags(article.tags),
    rich_text_body: article.rich_text_body || '',
    discipline: article.discipline || '',
    technote_type: article.technote_type || '',
    scope: article.scope || '',
    customer: article.customer || '',
    confidentiality: article.confidentiality || '',
    brand: article.brand || '',
    product_family: article.product_family || '',
    model_number: article.model_number || '',
    asset_type: article.asset_type || '',
    technology_type: article.technology_type || '',
    machine: article.machine || '',
    version: article.version || '',
    problem: article.problem || '',
    symptoms: article.symptoms || '',
    root_cause: article.root_cause || '',
    solution: article.solution || '',
    troubleshooting_notes: parseTroubleshootingRows(article.troubleshooting_notes),
    external_links: article.external_links || '',
    author_id: article.author_id || null,
    reviewer_id: article.reviewer_id || null,
  }
}

export function buildArticlePayload(
  form: ArticleFormState,
  { isCustomerSpecific, pendingDeletedAttachmentIds }: BuildArticlePayloadOptions,
): ArticleFormPayload {
  return {
    ...form,
    customer: isCustomerSpecific ? form.customer : 'general-customer',
    deleted_attachment_ids: pendingDeletedAttachmentIds,
    tags: form.tags,
    troubleshooting_notes: form.troubleshooting_notes.filter(
      row => row.issue || row.possible_cause || row.recommended_action,
    ),
    author_id: form.author_id || null,
    reviewer_id: form.reviewer_id || null,
    approver_id: null,
  }
}

export function buildAutoTags(form: ArticleFormState) {
  return [
    form.discipline && `discipline-${form.discipline}`,
    form.customer && `customer-${form.customer}`,
    form.brand && `brand-${form.brand}`,
    form.asset_type && `asset-${form.asset_type}`,
    form.technote_type && `doc-${form.technote_type}`,
  ].filter(Boolean)
}
